import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../utils/api';

/**
 * EnrollmentPlanManager
 * ----------------------
 * Lists, creates and manages recurring booking plans:
 *
 *   • all_year_round   — book every matching session, including holidays
 *   • term_time_only   — book every matching session except school holidays
 *   • ad_hoc           — no auto-booking; parent picks dates manually
 *
 * Used in:
 *   • Admin dashboard (mode='admin') — can create plans on behalf of any
 *     parent in the org, sees parent name/email on each row.
 *   • Parent dashboard (mode='parent') — restricted to own children.
 *
 * Backend reference: /app/backend/enrollment_plans.py
 */

const PATTERN_LABELS = {
  all_year_round: 'All year round',
  term_time_only: 'Term time only',
  ad_hoc: 'Ad-hoc (manual)',
};
const PATTERN_HINTS = {
  all_year_round: 'Books every matching session, including school holidays.',
  term_time_only: 'Books every matching session, but skips dates flagged as school holidays in your school calendar.',
  ad_hoc: 'No auto-booking. Useful as a placeholder to keep the family\'s preferred days on file.',
};
const STATUS_TONES = {
  active:    { bg: 'hsl(140 50% 92%)', fg: 'hsl(140 60% 25%)', label: 'Active' },
  paused:    { bg: 'hsl(40 90% 92%)',  fg: 'hsl(40 70% 30%)',  label: 'Paused' },
  cancelled: { bg: 'hsl(0 0% 92%)',    fg: 'hsl(0 0% 30%)',    label: 'Cancelled' },
};
const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const StatusPill = ({ status }) => {
  const t = STATUS_TONES[status] || STATUS_TONES.cancelled;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.15rem 0.5rem',
        borderRadius: 4,
        background: t.bg,
        color: t.fg,
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
      data-testid={`plan-status-${status}`}
    >
      {t.label}
    </span>
  );
};

export const EnrollmentPlanManager = ({ mode = 'parent' }) => {
  const isAdmin = mode === 'admin';

  // Data state
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [parents, setParents] = useState([]);          // admin only
  const [parentChildren, setParentChildren] = useState([]); // children of selected parent (admin) or own children (parent)
  const [templates, setTemplates] = useState([]);

  // Modal / form state
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return {
      parent_id: '',         // admin only
      child_ids: [],          // multi-child — was `child_id` (single)
      session_template_ids: [],
      days_of_week: [],
      pattern: 'all_year_round',
      start_date: new Date().toISOString().slice(0, 10),
      end_date: '',
      notes: '',
    };
  }

  // Initial load
  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [plansData, tplData] = await Promise.all([
        api.getEnrollmentPlans(),
        api.getSessionTemplates().catch(() => []),
      ]);
      setPlans(plansData || []);
      setTemplates(tplData || []);
      if (isAdmin) {
        const usersData = await api.getUsers().catch(() => []);
        setParents((usersData || []).filter(u => u.role === 'parent' && u.approval_status !== 'pending' && u.approval_status !== 'rejected'));
      } else {
        const kids = await api.getChildren().catch(() => []);
        setParentChildren(kids || []);
      }
    } catch (e) {
      setError(e.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { reload(); }, [reload]);

  // When admin picks a parent, fetch that parent's children
  useEffect(() => {
    if (!isAdmin || !form.parent_id) { return; }
    let cancelled = false;
    (async () => {
      try {
        const kids = await api.getUserChildren(form.parent_id);
        if (!cancelled) setParentChildren(kids || []);
      } catch (e) {
        if (!cancelled) setParentChildren([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, form.parent_id]);

  const openCreate = () => {
    setForm(emptyForm());
    setShowCreate(true);
  };

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter(x => x !== d)
        : [...f.days_of_week, d].sort(),
    }));
  };

  const toggleTemplate = (tid) => {
    setForm((f) => ({
      ...f,
      session_template_ids: f.session_template_ids.includes(tid)
        ? f.session_template_ids.filter(x => x !== tid)
        : [...f.session_template_ids, tid],
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (isAdmin && !form.parent_id) { setError('Pick a parent.'); return; }
    if (!form.child_ids || form.child_ids.length === 0) { setError('Pick at least one child.'); return; }
    if (!form.session_template_ids.length) { setError('Pick at least one session template.'); return; }
    if (!form.days_of_week.length) { setError('Pick at least one day of the week.'); return; }
    setSubmitting(true);
    setError('');
    try {
      // Always go through the batch endpoint so single-child and
      // multi-child flows share one code path. The server creates one
      // independent plan per child — future edits / pauses stay
      // per-child.
      const payload = {
        child_ids: form.child_ids,
        session_template_ids: form.session_template_ids,
        days_of_week: form.days_of_week,
        pattern: form.pattern,
        notes: form.notes || null,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      };
      if (isAdmin) payload.on_behalf_of_parent_id = form.parent_id;
      const result = await api.createEnrollmentPlansBatch(payload);
      // The backend kicks off materialisation in the background so the
      // create endpoint returns instantly. We close the modal, then
      // re-fetch the plan list a couple of times so the user sees the
      // last_run_summary populate without having to manually refresh.
      setShowCreate(false);
      const createdCount = result?.created?.length || 0;
      const errCount = result?.errors?.length || 0;
      let summary = `✅ ${createdCount} recurring plan${createdCount === 1 ? '' : 's'} created.`;
      if (errCount) {
        summary += ` ⚠️ ${errCount} could not be created (first issue: ${result.errors[0].message}).`;
      }
      summary += ' Booking the matching sessions in the background — they will appear shortly.';
      alert(summary);
      reload();
      // Soft re-poll so the summary lights up without manual refresh.
      setTimeout(() => { reload(); }, 4000);
      setTimeout(() => { reload(); }, 9000);
    } catch (err) {
      setError(err.message || 'Failed to create plan');
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (plan, status) => {
    if (status === 'cancelled' && !window.confirm('Cancel this recurring plan? Existing bookings will remain — cancel them individually if needed.')) return;
    try {
      if (status === 'cancelled') {
        await api.cancelEnrollmentPlan(plan._id);
      } else {
        await api.updateEnrollmentPlan(plan._id, { status });
      }
      reload();
    } catch (err) {
      alert(err.message || 'Failed');
    }
  };

  const runNow = async (plan) => {
    try {
      const res = await api.runEnrollmentPlan(plan._id);
      const parts = [];
      if (res.confirmed) parts.push(`${res.confirmed} confirmed`);
      if (res.already_booked) parts.push(`${res.already_booked} already booked`);
      if (res.waitlisted) parts.push(`${res.waitlisted} waitlisted`);
      if (res.skipped) parts.push(`${res.skipped} skipped (holidays/no-instance)`);
      alert(`Run complete: ${parts.join(' · ') || 'nothing to do'}`);
      reload();
    } catch (err) {
      alert(err.message || 'Failed');
    }
  };

  // Render -----------------------------------------------------------------

  const dayLabels = useMemo(() => (daysArr = []) => daysArr
    .map(d => DAYS_OF_WEEK.find(x => x.value === d)?.label)
    .filter(Boolean)
    .join(', '), []);

  if (loading) return <div className="card" data-testid="plans-loading">Loading recurring plans…</div>;

  return (
    <div data-testid="enrollment-plan-manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '0.75rem', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div>
          <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))' }}>
            Set up a recurring booking once and the platform fills in the calendar for you. The scheduler runs daily and is also re-run when a plan is created, so confirmations are immediate.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate} data-testid="create-plan-btn">
          + New recurring plan
        </button>
      </div>

      {error && (
        <div className="card" style={{ color: 'hsl(0 70% 35%)' }} data-testid="plans-error">{error}</div>
      )}

      {plans.length === 0 ? (
        <div className="card empty-state" data-testid="plans-empty">
          <div style={{ fontSize: '2rem' }}>📅</div>
          <h3>No recurring plans yet</h3>
          <p>
            Create one to enrol a child for an All-Year-Round, Term-Time-Only or Ad-hoc schedule.
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  {isAdmin && <th>Parent</th>}
                  <th>Child</th>
                  <th>Pattern</th>
                  <th>Days</th>
                  <th>Sessions</th>
                  <th>Window</th>
                  <th>Last run</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => (
                  <tr key={p._id} data-testid={`plan-row-${p._id}`}>
                    <td><StatusPill status={p.status} /></td>
                    {isAdmin && (
                      <td>
                        <strong>{p.parent_name || '—'}</strong>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{p.parent_email}</div>
                      </td>
                    )}
                    <td>{p.child_name || '—'}</td>
                    <td>{PATTERN_LABELS[p.pattern] || p.pattern}</td>
                    <td>{dayLabels(p.days_of_week) || '—'}</td>
                    <td>{(p.session_template_names || []).join(', ') || '—'}</td>
                    <td style={{ fontSize: '0.8rem' }}>
                      {p.start_date ? new Date(p.start_date).toLocaleDateString() : '—'}
                      {' → '}
                      {p.end_date ? new Date(p.end_date).toLocaleDateString() : 'open'}
                    </td>
                    <td style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                      {p.last_run_summary
                        ? `${p.last_run_summary.confirmed || 0} confirmed${p.last_run_summary.skipped ? ` · ${p.last_run_summary.skipped} skipped` : ''}`
                        : '—'}
                      <div>{p.last_run_at ? new Date(p.last_run_at).toLocaleString() : ''}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                        {p.status === 'active' && (
                          <>
                            <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }}
                              onClick={() => setStatus(p, 'paused')} data-testid={`plan-pause-${p._id}`}>
                              Pause
                            </button>
                            <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }}
                              onClick={() => runNow(p)} data-testid={`plan-run-${p._id}`}>
                              Run now
                            </button>
                          </>
                        )}
                        {p.status === 'paused' && (
                          <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }}
                            onClick={() => setStatus(p, 'active')} data-testid={`plan-resume-${p._id}`}>
                            Resume
                          </button>
                        )}
                        {p.status !== 'cancelled' && (
                          <button className="btn btn-ghost" style={{ fontSize: '0.8rem', color: 'hsl(0 70% 35%)' }}
                            onClick={() => setStatus(p, 'cancelled')} data-testid={`plan-cancel-${p._id}`}>
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create plan modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => !submitting && setShowCreate(false)} data-testid="plan-create-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }} data-testid="plan-create-modal">
            <div className="modal-header">
              <h2 style={{ margin: 0 }}>New recurring plan</h2>
            </div>
            <form onSubmit={submit} autoComplete="off">
              {isAdmin && (
                <div className="form-group">
                  <label>Parent</label>
                  <select
                    value={form.parent_id}
                    onChange={(e) => setForm({ ...form, parent_id: e.target.value, child_ids: [] })}
                    required
                    data-testid="plan-parent-select"
                  >
                    <option value="">— Select a parent —</option>
                    {parents.map(p => (
                      <option key={p._id} value={p._id}>
                        {p.full_name || '—'} · {p.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>
                  Child{(parentChildren || []).filter(c => !c.is_archived).length > 1 ? 'ren (tick one or more)' : ''}
                </label>
                {(parentChildren || []).filter(c => !c.is_archived).length === 0 ? (
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {isAdmin && !form.parent_id
                      ? 'Pick a parent first to see their children.'
                      : 'No active children on this account yet.'}
                  </small>
                ) : (
                  <div
                    data-testid="plan-child-multi"
                    style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}
                  >
                    {(parentChildren || [])
                      .filter(c => !c.is_archived)
                      .map(c => {
                        const checked = (form.child_ids || []).includes(c._id);
                        return (
                          <label
                            key={c._id}
                            data-testid={`plan-child-checkbox-label-${c._id}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.4rem',
                              padding: '0.35rem 0.65rem',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '999px',
                              cursor: 'pointer',
                              userSelect: 'none',
                              background: checked ? 'hsl(var(--accent))' : 'hsl(var(--background))',
                              fontWeight: checked ? 600 : 400,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setForm(f => {
                                  const next = e.target.checked
                                    ? [...(f.child_ids || []), c._id]
                                    : (f.child_ids || []).filter(x => x !== c._id);
                                  return { ...f, child_ids: next };
                                });
                              }}
                              data-testid={`plan-child-checkbox-${c._id}`}
                            />
                            <span>
                              {c.first_name} {c.last_name}
                              {c.age_group ? ` (age group ${c.age_group})` : ''}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                )}
                {(form.child_ids || []).length > 1 && (
                  <small style={{ display: 'block', marginTop: 4, color: 'hsl(var(--muted-foreground))' }}>
                    A separate recurring plan will be created for each selected child, so
                    you can pause or cancel them individually later.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Sessions to enrol in</label>
                <div data-testid="plan-templates" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {templates.length === 0 && (
                    <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                      No session templates configured for your organisation yet.
                    </small>
                  )}
                  {templates.map((t) => (
                    <label key={t._id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <input
                        type="checkbox"
                        checked={form.session_template_ids.includes(t._id)}
                        onChange={() => toggleTemplate(t._id)}
                        data-testid={`plan-template-${t._id}`}
                      />
                      <span>
                        <strong>{t.name}</strong>{' '}
                        <span style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                          {t.start_time}–{t.end_time}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Days of the week</label>
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }} data-testid="plan-days">
                  {DAYS_OF_WEEK.map((d) => {
                    const on = form.days_of_week.includes(d.value);
                    return (
                      <button
                        type="button"
                        key={d.value}
                        className={on ? 'btn btn-primary' : 'btn btn-ghost'}
                        style={{ fontSize: '0.85rem', padding: '0.35rem 0.65rem' }}
                        onClick={() => toggleDay(d.value)}
                        data-testid={`plan-day-${d.value}`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label>Pattern</label>
                <select
                  value={form.pattern}
                  onChange={(e) => setForm({ ...form, pattern: e.target.value })}
                  data-testid="plan-pattern-select"
                >
                  {Object.entries(PATTERN_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
                <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                  {PATTERN_HINTS[form.pattern]}
                </small>
              </div>

              <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                <div>
                  <label>Start date</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    data-testid="plan-start-date"
                  />
                </div>
                <div>
                  <label>End date (optional)</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    data-testid="plan-end-date"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="plan-notes"
                />
              </div>

              {error && (
                <div style={{ color: 'hsl(0 70% 35%)', marginBottom: '0.5rem' }} data-testid="plan-form-error">
                  {error}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" disabled={submitting}
                  onClick={() => setShowCreate(false)} data-testid="plan-cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="plan-submit">
                  {submitting ? 'Creating…' : 'Create plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrollmentPlanManager;
